"use client"

import { useEffect, useRef, useState } from "react"

export default function AboutUs() {
  const canvasRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const particlesRef = useRef([])
  const animationIdRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)

    // Initialize floating particles (representing dates)
    const initParticles = () => {
      particlesRef.current = Array.from({ length: 15 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 8 + 4,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.3 + 0.1,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
      }))
    }

    initParticles()

    const animate = () => {
      // Clear canvas with semi-transparent background for trailing effect
      ctx.fillStyle = "rgba(245, 230, 211, 0.02)"
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw and update particles
      particlesRef.current.forEach((particle) => {
        // Update position
        particle.x += particle.vx
        particle.y += particle.vy
        particle.rotation += particle.rotationSpeed

        // Bounce off walls
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1

        // Keep within bounds
        particle.x = Math.max(0, Math.min(canvas.width, particle.x))
        particle.y = Math.max(0, Math.min(canvas.height, particle.y))

        // Draw particle (date-like circle with gradient)
        ctx.save()
        ctx.globalAlpha = particle.opacity
        ctx.translate(particle.x, particle.y)
        ctx.rotate(particle.rotation)

        // Create gradient for date appearance
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, particle.size)
        gradient.addColorStop(0, "#D4AF37")
        gradient.addColorStop(0.5, "#8B7500")
        gradient.addColorStop(1, "#654321")

        ctx.fillStyle = gradient
        ctx.fillRect(-particle.size, -particle.size, particle.size * 2, particle.size * 2)
        ctx.borderRadius = "50%"

        ctx.restore()
      })

      animationIdRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener("resize", resizeCanvas)
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setIsVisible(true)

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in")
        }
      })
    }, observerOptions)

    document.querySelectorAll("[data-animate]").forEach((el) => {
      observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5E6D3] via-[#FAF3E0] to-[#F0E5D8] overflow-hidden relative">
      <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-60 z-0" />

      {/* Background gradient overlay */}
      <div className="fixed inset-0 bg-gradient-to-br from-transparent via-[#FAF3E0]/30 to-[#E8D4B8]/20 pointer-events-none z-0" />

      {/* Main content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div
              className="space-y-6 animate-in fade-in duration-1000"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transition: "all 1s ease-out",
              }}
            >
  <div className="inline-block text-center">
  {/* Main Brand Name */}
  <span
    className="block text-5xl sm:text-6xl lg:text-7xl font-serif font-light tracking-tight 
               bg-gradient-to-r from-[#8B4513] via-[#D4AF37] to-[#654321] 
               bg-clip-text text-transparent drop-shadow-md"
    style={{
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundImage:
        "linear-gradient(90deg, #8B4513 0%, #D4AF37 50%, #654321 100%)",
    }}
  >
    Saudi Dates
  </span>

  {/* Common Name / Subtitle */}
  <span
    className="block text-xl sm:text-2xl font-serif font-light mt-3 
               bg-gradient-to-r from-[#654321] via-[#D4AF37] to-[#8B4513] 
               bg-clip-text text-transparent tracking-wide"
    style={{
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundImage:
        "linear-gradient(90deg, #654321 0%, #D4AF37 50%, #8B4513 100%)",
    }}
  >
    Dry Fruits & Attar
  </span>
</div>



              <p className="text-xl sm:text-2xl text-[#654321] font-light leading-relaxed max-w-2xl mx-auto">
                Handpicked premium dates from the sacred gardens of Madinah Al Munawwarah
              </p>

              <div className="flex flex-wrap justify-center gap-4 pt-6">
                <span className="px-6 py-2 bg-white/40 backdrop-blur-sm rounded-full text-[#654321] font-medium border border-[#D4AF37]/20">
                  100% Natural
                </span>
                <span className="px-6 py-2 bg-white/40 backdrop-blur-sm rounded-full text-[#654321] font-medium border border-[#D4AF37]/20">
                  Ethically Sourced
                </span>
                <span className="px-6 py-2 bg-white/40 backdrop-blur-sm rounded-full text-[#654321] font-medium border border-[#D4AF37]/20">
                  Premium Quality
                </span>
              </div>
            </div>

            <div className="pt-12 animate-bounce">
              <svg className="w-6 h-6 mx-auto text-[#8B4513]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </section>

        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/30 backdrop-blur-md">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div
              data-animate
              className="space-y-8 opacity-0"
              style={{
                animation: isVisible ? "slideInLeft 1s ease-out 0.2s forwards" : "none",
              }}
            >
              <h2 className="text-4xl sm:text-5xl font-serif font-light text-[#654321]">Our Heritage</h2>

              <p className="text-lg text-[#654321]/80 leading-relaxed">
                For generations, the fertile lands of Madinah Al Munawwarah have produced the world's most exquisite
                dates. At Saudi Dates, we honor this ancient tradition by handpicking only the finest varieties from our
                heritage farms.
              </p>

              <p className="text-lg text-[#654321]/80 leading-relaxed">
                Our commitment to excellence means every date is carefully selected, naturally processed, and packed
                with integrity. We bring the authentic taste of Saudi Arabia directly to your table—no compromises, no
                additives, just pure, natural goodness.
              </p>

              <div className="space-y-4 pt-6">
                <h3 className="text-2xl font-serif font-light text-[#8B4513]">Our Varieties</h3>
                <div className="grid grid-cols-2 gap-4">
                  {["Ajwa", "Aseel", "Medjool", "Rabbi"].map((variety) => (
                    <div
                      key={variety}
                      className="p-4 bg-gradient-to-br from-[#F5E6D3] to-[#FAF3E0] rounded-lg border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 transition-all duration-300"
                    >
                      <span className="text-[#654321] font-medium">{variety}</span>
                      <p className="text-sm text-[#654321]/60 mt-1">Premium Grade</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              data-animate
              className="relative h-96 lg:h-full min-h-96 opacity-0"
              style={{
                animation: isVisible ? "slideInRight 1s ease-out 0.4s forwards" : "none",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 to-[#8B4513]/20 rounded-3xl backdrop-blur-sm border border-[#D4AF37]/30" />

              <div className="absolute inset-8 bg-gradient-to-br from-[#F5E6D3] to-[#FAF3E0] rounded-2xl shadow-2xl flex items-center justify-center overflow-hidden">
                <div className="relative w-full h-full flex items-center justify-center">
                  {/* Floating date decoration */}
                  <div
                    className="absolute text-9xl opacity-20"
                    style={{
                      animation: "float 6s ease-in-out infinite",
                    }}
                  >
                    🌴
                  </div>

                  <div className="relative z-10 text-center space-y-4">
                    <p className="text-6xl font-serif text-[#8B4513]">25+</p>
                    <p className="text-xl font-light text-[#654321]">Years of Excellence</p>
                    <p className="text-sm text-[#654321]/60">Serving customers worldwide</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-serif font-light text-[#654321] mb-6">Our Core Values</h2>
              <p className="text-lg text-[#654321]/70 max-w-2xl mx-auto">
                Every date we deliver represents our unwavering commitment to quality, authenticity, and customer
                satisfaction
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "🌾",
                  title: "Natural & Pure",
                  description:
                    "No artificial additives, no processing aids. Just 100% natural dates handpicked from our heritage farms in Madinah.",
                },
                {
                  icon: "🤝",
                  title: "Fair Trade Practices",
                  description:
                    "We support local farmers and ensure ethical sourcing throughout our entire supply chain.",
                },
                {
                  icon: "✨",
                  title: "Premium Quality",
                  description:
                    "Every batch undergoes strict quality control to ensure only the finest dates reach your table.",
                },
                {
                  icon: "🚚",
                  title: "Reliable Delivery",
                  description:
                    "Nationwide delivery with careful packaging to preserve freshness and taste from farm to your door.",
                },
                {
                  icon: "💡",
                  title: "Innovation",
                  description:
                    "Modern farming techniques combined with traditional wisdom for consistently excellent results.",
                },
                {
                  icon: "❤️",
                  title: "Customer First",
                  description:
                    "Your satisfaction is our priority. Wholesale and retail options tailored to your needs.",
                },
              ].map((value, idx) => (
                <div
                  key={idx}
                  data-animate
                  className="group opacity-0 p-8 rounded-2xl bg-gradient-to-br from-white/40 to-white/20 backdrop-blur-md border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
                  style={{
                    animation: isVisible ? `slideInUp 0.8s ease-out ${0.2 + idx * 0.1}s forwards` : "none",
                  }}
                >
                  <div className="text-5xl mb-4">{value.icon}</div>
                  <h3 className="text-2xl font-serif font-light text-[#654321] mb-3">{value.title}</h3>
                  <p className="text-[#654321]/70 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
  <div className="max-w-6xl mx-auto">
    <div className="text-center mb-16">
      <h2 className="text-4xl sm:text-5xl font-serif font-light text-[#654321] mb-6">
        Why Choose Saudi Dates
      </h2>
      <p className="text-lg text-[#654321]/70 max-w-2xl mx-auto">
        Experience unmatched quality, freshness, and authenticity — rooted in our heritage and perfected with care.
      </p>
    </div>

    <div className="space-y-12">
      {[
        {
          title: "Direct from the Source",
          content:
            "We eliminate middlemen to bring you authentic Saudi dates straight from Madinah's finest gardens. This ensures superior freshness and quality that mass-market suppliers simply cannot match.",
          highlights: ["Farm to Table", "Zero Additives", "Freshness Guaranteed"],
        },
        {
          title: "Wholesale & Retail Solutions",
          content:
            "Whether you need bulk orders for your business or premium selections for personal consumption, we provide flexible solutions with competitive pricing.",
          highlights: ["Bulk Discounts", "Customizable Packages", "Business Support"],
        },
        {
          title: "Expert Selection & Curation",
          content:
            "Our team of date experts personally selects each variety based on size, color, moisture, and taste. Only the top tier makes it through our rigorous inspection process.",
          highlights: ["Hand-Selected", "Quality Assured", "Premium Grade"],
        },
      ].map((section, idx) => (
        <div
          key={idx}
          data-animate
          className="opacity-0 p-8 sm:p-12 rounded-2xl bg-gradient-to-br from-[#F5E6D3] to-[#FAF3E0] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 hover:shadow-xl transition-all duration-500 group relative overflow-hidden"
          style={{
            animation: isVisible ? `slideInUp 0.8s ease-out ${0.3 + idx * 0.15}s forwards` : "none",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative z-10">
            <h3 className="text-2xl sm:text-3xl font-serif font-light text-[#8B4513] mb-4">
              {section.title}
            </h3>
            <p className="text-lg text-[#654321]/80 leading-relaxed mb-6">
              {section.content}
            </p>
            <div className="flex flex-wrap gap-3">
              {section.highlights.map((highlight) => (
                <span
                  key={highlight}
                  className="px-4 py-2 bg-gradient-to-r from-[#D4AF37]/10 to-[#8B4513]/10 rounded-full text-[#654321] font-medium border border-[#D4AF37]/40 hover:border-[#D4AF37]/70 transition-all"
                >
                  {highlight}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>


        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-serif font-light text-[#654321] mb-6">
                Our Excellence in Every Date
              </h2>
              <p className="text-lg text-[#654321]/70 max-w-2xl mx-auto">
                Each variety we offer represents generations of careful cultivation and our commitment to deliver the
                finest
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  variety: "Ajwa",
                  description:
                    "The king of dates. Soft, rich, and deeply satisfying with a unique caramel-like flavor.",
                  benefits: ["Rich in antioxidants", "Premium taste", "Natural sweetness"],
                },
                {
                  variety: "Aseel",
                  description: "Crispy and crunchy with a delicate sweetness. Perfect for those who enjoy texture.",
                  benefits: ["Energizing", "Long shelf life", "Great for snacking"],
                },
                {
                  variety: "Medjool",
                  description: "Large, moist, and incredibly satisfying. The most popular choice for pure indulgence.",
                  benefits: ["Large and meaty", "Natural moisture", "Incredibly sweet"],
                },
                {
                  variety: "Rabbi",
                  description: "Early season dates with distinct flavor. A rare find for the true date connoisseur.",
                  benefits: ["Unique flavor", "Limited availability", "Premium selection"],
                },
              ].map((product, idx) => (
                <div
                  key={idx}
                  data-animate
                  className="opacity-0 p-8 rounded-2xl bg-gradient-to-br from-[#F5E6D3] to-[#FAF3E0] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 hover:shadow-xl transition-all duration-500 overflow-hidden group"
                  style={{
                    animation: isVisible ? `slideInUp 0.8s ease-out ${0.2 + idx * 0.1}s forwards` : "none",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative z-10">
                    <h3 className="text-3xl font-serif font-light text-[#8B4513] mb-3">{product.variety}</h3>
                    <p className="text-[#654321]/80 leading-relaxed mb-6">{product.description}</p>

                    <div className="space-y-2">
                      {product.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-center gap-3">
                          <span className="text-[#D4AF37]">✓</span>
                          <span className="text-[#654321]/70">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/40 backdrop-blur-md">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl sm:text-5xl font-serif font-light text-[#654321] text-center mb-16">
              Our Sourcing & Quality Promise
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
              {[
                {
                  step: "01",
                  title: "Careful Selection",
                  description:
                    "Our experts visit the gardens during peak harvest season to hand-select the finest dates based on rigorous standards.",
                },
                {
                  step: "02",
                  title: "Quality Testing",
                  description:
                    "Every batch undergoes comprehensive testing for moisture, taste, color, and freshness before processing.",
                },
                {
                  step: "03",
                  title: "Expert Processing",
                  description:
                    "Using traditional methods combined with modern hygiene standards, we preserve the natural quality and nutrition.",
                },
                {
                  step: "04",
                  title: "Careful Packaging",
                  description:
                    "Premium packaging protects dates from light and moisture, ensuring they reach you in perfect condition.",
                },
                {
                  step: "05",
                  title: "Track & Trace",
                  description:
                    "Every order is tracked throughout the journey to ensure reliability and customer satisfaction.",
                },
                {
                  step: "06",
                  title: "Customer Care",
                  description: "Our support team is ready to assist with any inquiries about our products or services.",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  data-animate
                  className="opacity-0 p-8 rounded-xl bg-gradient-to-br from-white/60 to-white/30 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-500 text-center"
                  style={{
                    animation: isVisible ? `slideInUp 0.8s ease-out ${0.2 + (idx % 3) * 0.15}s forwards` : "none",
                  }}
                >
                  <div className="text-5xl font-serif font-light text-[#D4AF37] mb-4">{item.step}</div>
                  <h3 className="text-xl font-serif font-light text-[#654321] mb-3">{item.title}</h3>
                  <p className="text-[#654321]/70 leading-relaxed text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

<section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#FAF3E0] to-[#F5E6D3]">
  <div className="max-w-4xl mx-auto text-center">
    {/* Section Heading */}
    <h2 className="text-4xl sm:text-5xl font-serif font-light text-[#654321] mb-6">
      Connect With Us
    </h2>
    <p className="text-lg text-[#654321]/80 mb-12 leading-relaxed max-w-2xl mx-auto">
      Whether you're looking for wholesale opportunities, retail purchases, or have special inquiries about our premium dates,
      we're here to serve you with care and excellence.
    </p>

    {/* Contact Cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
      {[
        {
          icon: "📧",
          title: "Email Us",
          content: (
            <a
              href="mailto:imranandsonstraders@gmail.com"
              className="text-[#8B4513] hover:text-[#D4AF37] transition-colors font-medium"
            >
              imranandsonstraders@gmail.com
            </a>
          ),
        },
        {
          icon: "📞",
          title: "Call / WhatsApp",
          content: (
            <a
              href="https://wa.me/447956575153"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8B4513] hover:text-[#D4AF37] transition-colors font-medium"
            >
              +44 7956 575153
            </a>
          ),
        },
        {
          icon: "📦",
          title: "Bulk Orders",
          content:
            "Special wholesale packages available for businesses, restaurants, and large-scale retail operations. Contact us for custom orders.",
        },
      ].map((item, idx) => (
        <div
          key={idx}
          data-animate
          className="relative p-8 rounded-2xl bg-white/40 backdrop-blur-md border border-[#D4AF37]/30 
                     hover:border-[#D4AF37]/60 hover:shadow-xl transition-all duration-500 overflow-hidden group"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transition: `all 0.8s ease-out ${0.2 + idx * 0.15}s`,
          }}
        >
          {/* Subtle shine effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />

          <div className="relative z-10 text-center">
            <p className="text-3xl sm:text-4xl mb-3">{item.icon}</p>
            <h3 className="text-2xl font-serif font-light text-[#8B4513] mb-3">{item.title}</h3>
            <p className="text-[#654321]/80 text-lg leading-relaxed">{item.content}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Call-to-Action Button */}
    <div className="inline-block">
      <button
        className="px-10 py-4 rounded-full text-lg font-semibold text-white 
                   bg-gradient-to-r from-[#C19A6B] via-[#8B4513] to-[#654321] 
                   shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-500"
      >
        Get in Touch
      </button>
    </div>
  </div>
</section>



      
      </div>

      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }

        .animate-in.fade-in {
          animation: fadeIn 1s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-bounce {
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  )
}
